export type ReservationRecoveryState =
  | 'ACTIVE_GENERATION'
  | 'AWAITING_PHYSICAL_EXTRACTION'
  | 'CONSUMED'
  | 'RELEASED';

/** Client-side mirror of server reservation TTL (15 minutes). */
export const RESERVATION_RECOVERY_TTL_MS = 15 * 60 * 1000;

export interface PhysicalRecoveryIdentity {
  projectId: string;
  chapterNumber: number;
  runId: string;
  reservationIdOrRunId: string;
  reservationRecoveryState: 'AWAITING_PHYSICAL_EXTRACTION';
  safeResumePoint: 'PHYSICAL_CONTINUITY_EXTRACTION';
  checkpointPath?: string;
  createdAt: string;
  expiresAt: string;
}

export function buildPhysicalRecoveryExpiresAt(now = Date.now()): string {
  return new Date(now + RESERVATION_RECOVERY_TTL_MS).toISOString();
}

export function buildPhysicalRecoveryIdentity(params: {
  projectId: string;
  chapterNumber: number;
  runId: string;
  checkpointPath?: string;
  now?: number;
}): PhysicalRecoveryIdentity {
  const createdAt = new Date(params.now ?? Date.now()).toISOString();
  return {
    projectId: params.projectId,
    chapterNumber: params.chapterNumber,
    runId: params.runId,
    reservationIdOrRunId: params.runId,
    reservationRecoveryState: 'AWAITING_PHYSICAL_EXTRACTION',
    safeResumePoint: 'PHYSICAL_CONTINUITY_EXTRACTION',
    checkpointPath: params.checkpointPath,
    createdAt,
    expiresAt: buildPhysicalRecoveryExpiresAt(params.now ?? Date.now()),
  };
}

export type GenerationCallbackResult =
  | boolean
  | {
      success: boolean;
      reservationDisposition?: 'consume' | 'release' | 'retain_awaiting_physical';
    };

export function normalizeGenerationCallbackResult(
  result: GenerationCallbackResult,
): { success: boolean; reservationDisposition: 'consume' | 'release' | 'retain_awaiting_physical' } {
  if (typeof result === 'boolean') {
    return {
      success: result,
      reservationDisposition: result ? 'consume' : 'release',
    };
  }
  return {
    success: result.success,
    reservationDisposition:
      result.reservationDisposition || (result.success ? 'consume' : 'release'),
  };
}

export function resolveReservationRecoveryState(params: {
  reservationMade: boolean;
  disposition: 'consume' | 'release' | 'retain_awaiting_physical';
  priorState?: ReservationRecoveryState;
}): ReservationRecoveryState {
  if (!params.reservationMade) return params.priorState || 'RELEASED';
  if (params.disposition === 'consume') return 'CONSUMED';
  if (params.disposition === 'retain_awaiting_physical') return 'AWAITING_PHYSICAL_EXTRACTION';
  return 'RELEASED';
}

export function shouldSkipReservationForRecovery(params: {
  reuseReservationRunId?: string;
  recoveryState?: ReservationRecoveryState;
}): boolean {
  return Boolean(
    params.reuseReservationRunId &&
    params.recoveryState === 'AWAITING_PHYSICAL_EXTRACTION',
  );
}

export function isReservationRecoveryExpired(
  pausedAt?: string,
  now = Date.now(),
  expiresAt?: string,
): boolean {
  if (expiresAt) {
    const expiryMs = Date.parse(expiresAt);
    if (Number.isFinite(expiryMs)) return now >= expiryMs;
  }
  if (!pausedAt) return false;
  const pausedMs = Date.parse(pausedAt);
  if (!Number.isFinite(pausedMs)) return false;
  return now - pausedMs > RESERVATION_RECOVERY_TTL_MS;
}

export function shouldReleaseReservationOnRecoveryCancel(
  recoveryState?: ReservationRecoveryState,
): boolean {
  return recoveryState === 'AWAITING_PHYSICAL_EXTRACTION';
}
